import { Box, Tooltip, Typography } from '@montugroup/design-system';
import { useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import { useMemo, useRef } from 'react';

export type TimelinePoint = {
  formattedDate: string;
  isoDate: string;
  value: number;
  shouldStopProgress?: boolean;
  label?: ReactNode;
};

function getProgressPercentage({ start, middle, end }: { start: number; middle: number; end: number }): number {
  if (!Number.isFinite(start)) {
    throw new TypeError('start must be a finite number');
  }

  if (!Number.isFinite(middle)) {
    throw new TypeError('middle must be a finite number');
  }

  if (!Number.isFinite(end)) {
    throw new TypeError('end must be a finite number');
  }

  // This handles edgecase where interval is 1 day long, unlikely to happen
  if (start === end) {
    return 0;
  }

  if (start > end) {
    throw new RangeError('start must be before or equal to end');
  }

  if (middle < start || middle > end) {
    throw new RangeError('middle must be between start and end (inclusive)');
  }

  return ((middle - start) / (end - start)) * 100;
}

const isoDateToStartOfDayMillis = (isoDate: string) => {
  return DateTime.fromISO(isoDate, { setZone: true }).startOf('day').toMillis();
};

const POINT_SIZE = 18;
const BAR_HEIGHT = 8;
const POINT_TOP = -((POINT_SIZE - BAR_HEIGHT) / 2);

function Bar({ height, width, color }: { height: number; width?: number; color: string }) {
  const defaultWidth = 100;

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        width: `${width ?? defaultWidth}%`,
        top: 0,
        height,
        bgcolor: color,
        borderRadius: height / 2 / 4,
      }}
    />
  );
}

function Point({
  point,
}: {
  point: TimelinePoint & { positionInTimeline: number; correctedPositionInTimeline: string };
}) {
  const theme = useTheme();
  const colorPalette = point.shouldStopProgress ? theme.palette.secondary : theme.palette.primary;

  return (
    <>
      <Tooltip title={point.formattedDate} arrow>
        <Box
          sx={{
            cursor: 'default',
            position: 'absolute',
            left: point.correctedPositionInTimeline,
            top: POINT_TOP,
            transform: 'translateX(-50%)',
            width: POINT_SIZE,
            height: POINT_SIZE,
            borderRadius: '50%',
            bgcolor: colorPalette.main,
            color: colorPalette.contrastText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <Typography variant="overline" fontWeight={600}>
            {point.value}
          </Typography>
        </Box>
      </Tooltip>
      {point.label && (
        <Box
          sx={{
            position: 'absolute',
            left: `${point.positionInTimeline}%`,
            top: POINT_TOP + POINT_SIZE,
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
        >
          {point.label}
        </Box>
      )}
    </>
  );
}

export const Timeline = ({
  startDate,
  endDate,
  points,
}: {
  startDate: string;
  endDate: string;
  points: TimelinePoint[];
}) => {
  const theme = useTheme();

  // NOTE: Using millis of the date for easier calculations, plus it allows to go across year breakpoints
  const startDateMs = isoDateToStartOfDayMillis(startDate);
  const endDateMs = isoDateToStartOfDayMillis(endDate);
  const progressPointRef = useRef(100); // Default to a 100% complete timeline unless one of the points has `shouldStopProgress: true`

  const timelinePoints = useMemo(
    () =>
      points.map((point, i) => {
        const lastPoint = points[i - 1];
        const nextPoint = points[i + 1];

        const shouldMovePointSlightlyLeft = point.formattedDate === nextPoint?.formattedDate;
        const shouldMovePointSlightlyRight = point.formattedDate === lastPoint?.formattedDate;

        const positionInTimeline = getProgressPercentage({
          start: startDateMs,
          middle: isoDateToStartOfDayMillis(point.isoDate),
          end: endDateMs,
        });
        let correctedPositionInTimeline = `${positionInTimeline}%`;

        if (shouldMovePointSlightlyLeft) {
          correctedPositionInTimeline = `calc(${positionInTimeline}% - ${POINT_SIZE / 2}px)`;
        } else if (shouldMovePointSlightlyRight) {
          correctedPositionInTimeline = `calc(${positionInTimeline}% + ${POINT_SIZE / 2}px)`;
        }

        // HACK: This `correctedPositionInTimeline` related logic allows for 2 points to share the same date on the Timeline by moving slightly left or right to not overlap
        // The alternative to this hack would be reworking the timeline to handle arrays of points per date instead
        const result = { ...point, positionInTimeline, correctedPositionInTimeline };

        if (point.shouldStopProgress) {
          progressPointRef.current = positionInTimeline;
        }

        return result;
      }),
    [endDateMs, points, startDateMs],
  );

  return (
    <Box sx={{ width: '100%', height: POINT_SIZE, paddingLeft: `${POINT_SIZE / 2}px` }}>
      <Box sx={{ position: 'relative', top: Math.abs(POINT_TOP) }}>
        {/* Base bar */}
        <Bar height={BAR_HEIGHT} color={theme.palette.divider} />

        {/* Progress bar */}
        <Bar height={BAR_HEIGHT} color={theme.palette.secondary.main} width={progressPointRef.current} />

        {timelinePoints.map((point) => (
          <Point key={point.correctedPositionInTimeline} point={point} />
        ))}
      </Box>
    </Box>
  );
};
